import React, { createContext, useContext, useState, useCallback } from 'react';

const BugReportContext = createContext();

/**
 * BugReportContextProvider - Manages contextual information for bug reports
 * 
 * This provider allows components to register context that should be included
 * in bug reports (e.g., Document ID, Task ID). Components are responsible for
 * clearing their context when they unmount.
 * 
 * Usage in components:
 * ```javascript
 * const { setBugContext, clearBugContext } = useBugReportContext();
 * 
 * useEffect(() => {
 *   setBugContext(`Document ID: ${documentId}\n\n`);
 *   return () => clearBugContext();
 * }, [documentId]);
 * ```
 */
export const BugReportContextProvider = ({ children }) => {
  const [bugContext, setBugContextInternal] = useState(null);
  
  const setBugContext = useCallback((context) => {
    setBugContextInternal(context);
  }, []);
  
  const clearBugContext = useCallback(() => {
    setBugContextInternal(null);
  }, []);
  
  const value = {
    bugContext,
    setBugContext,
    clearBugContext
  };
  
  return (
    <BugReportContext.Provider value={value}>
      {children}
    </BugReportContext.Provider>
  );
};

/**
 * useBugReportContext - Hook to access bug report context
 * 
 * @returns {Object} { bugContext, setBugContext, clearBugContext }
 */
export const useBugReportContext = () => {
  const context = useContext(BugReportContext);
  if (!context) {
    throw new Error('useBugReportContext must be used within a BugReportContextProvider');
  }
  return context;
};
